import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Customer, CustomerListResponse, CreateCustomerRequest, UpdateCustomerRequest, CustomerSearchParams } from '../models/CustomerModel';
import { getApiBaseUrl } from '@/setup/apiBaseUrl';

export const customersApi = createApi({
  reducerPath: 'customersApi',
  baseQuery: fetchBaseQuery({
    baseUrl: getApiBaseUrl(),
    prepareHeaders: (headers, { getState, endpoint }) => {
      // Get token from Redux state if needed
      const state = getState() as any;
      const accessToken = state.auth?.accessToken;
      
      // Debug logging only in development and only once per session
      if (process.env.NODE_ENV === 'development' && !(window as any).customerApiDebugLogged) {
        console.log('RTK Query Auth Debug:', {
          endpoint,
          hasState: !!state,
          hasAuth: !!state.auth,
          hasAccessToken: !!accessToken,
          token: accessToken ? `${accessToken.substring(0, 20)}...` : 'No token'
        });
        (window as any).customerApiDebugLogged = true;
      }
      
      // Always add Authorization header if we have a token
      if (accessToken) {
        headers.set('authorization', `Bearer ${accessToken}`);
      }
      
      headers.set('content-type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['Customer'],
  keepUnusedDataFor: 300, // 5 minutes cache
  endpoints: (builder) => ({
    // Get customers list
    getCustomers: builder.query<CustomerListResponse, CustomerSearchParams | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params) {
          Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
              searchParams.append(key, String(value));
            }
          });
        }
        return `customers/?${searchParams.toString()}`;
      },
      providesTags: ['Customer'],
    }),
    
    // Get customer by ID
    getCustomerById: builder.query<Customer, string>({
      query: (id) => `customers/${id}/`,
      providesTags: (result, error, id) => [{ type: 'Customer', id }],
    }),
    
    // Create customer
    createCustomer: builder.mutation<Customer, CreateCustomerRequest>({
      query: (customerData) => ({
        url: 'customers/',
        method: 'POST',
        body: customerData,
      }),
      invalidatesTags: ['Customer'],
    }),
    
    // Update customer
    updateCustomer: builder.mutation<Customer, { id: string; data: UpdateCustomerRequest }>({
      query: ({ id, data }) => ({
        url: `customers/${id}/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Customer', id }, 'Customer'],
    }),
    
    // Delete customer
    deleteCustomer: builder.mutation<void, string>({
      query: (id) => ({
        url: `customers/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Customer', id }, 'Customer'],
    }),
    
    // Search customers
    searchCustomers: builder.query<CustomerListResponse, CustomerSearchParams>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            searchParams.append(key, String(value));
          }
        });
        return `customers/search/?${searchParams.toString()}`;
      },
      providesTags: ['Customer'],
    }),
  }),
});

export const {
  useGetCustomersQuery,
  useGetCustomerByIdQuery,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
  useDeleteCustomerMutation,
  useSearchCustomersQuery,
} = customersApi;
